import { useMutation } from "@tanstack/react-query";
import { Play } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { canonicalApi } from "@/lib/api-canonical";

import { CodeEditor } from "./CodeEditor";

const SAMPLE = `{
  "uid_riga": "R-0001",
  "uid_testata": "T-0001",
  "tipo_movimento": 1,
  "maga_id": "MAG01",
  "ubicazione_id": "U-01",
  "intestatario": "CLI001",
  "articolo": "ART-A100",
  "magaord_qnt": 10
}`;

export function TestPayloadPanel({
  canonical_type,
  version,
}: {
  canonical_type: string;
  version: number;
}) {
  const [json, setJson] = React.useState(SAMPLE);

  const mutation = useMutation({
    mutationFn: async () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(json);
      } catch (err) {
        throw new Error(`Invalid JSON: ${(err as Error).message}`);
      }
      return canonicalApi.test(canonical_type, version, parsed);
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 h-10 bg-surface">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Payload (JSON)
        </div>
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
          {mutation.isPending ? "Validating…" : "Validate"}
        </Button>
      </div>

      <div className="grid flex-1 grid-cols-2 overflow-hidden">
        <div className="border-r border-border">
          <CodeEditor value={json} language="json" onChange={setJson} />
        </div>
        <div className="flex flex-col overflow-hidden">
          <div className="flex h-9 items-center border-b border-border px-3 text-xs uppercase tracking-wider text-muted-foreground bg-surface">
            Result
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2">
            {mutation.error && (
              <Alert tone="danger" title="Request error">
                {(mutation.error as Error).message}
              </Alert>
            )}
            {mutation.data && (
              <>
                {mutation.data.valid ? (
                  <Alert tone="success" title="Payload is valid" />
                ) : (
                  <Alert tone="danger" title="Validation failed">
                    <ul className="list-disc pl-4">
                      {mutation.data.errors.map((e, i) => (
                        <li key={i} className="font-mono text-xs">{e}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {mutation.data.normalized && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Normalized
                    </div>
                    <div className="h-64 border border-border">
                      <CodeEditor
                        value={JSON.stringify(mutation.data.normalized, null, 2)}
                        language="json"
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {!mutation.data && !mutation.error && (
              <div className="text-xs text-muted-foreground">
                Click <span className="font-mono">Validate</span> to test the payload
                against this canonical version.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
